import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Section } from '../components/Section.js'
import { Card, CardBody, CardHeader, CardFooter } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input, Select, Textarea, Checkbox } from '../components/Field.js'
import { Money } from '../components/Money.js'
import { StatusPill } from '../components/StatusPill.js'

export const Route = createFileRoute('/_app/super/plans')({
  component: SuperPlansPage,
})

const FEATURE_KEYS = [
  'fee_collection',
  'attendance',
  'biometric_sync',
  'performance',
  'exam_boards',
  'parent_communication',
  'referrals',
  'white_label',
  'social_login',
  'teacher_accounts',
  'email_customisation',
  'reporting',
  'whatsapp_notifications',
] as const

type FeatureKey = (typeof FEATURE_KEYS)[number]

const FEATURE_LABELS: Record<FeatureKey, string> = {
  fee_collection: 'Fee collection',
  attendance: 'Attendance',
  biometric_sync: 'Biometric sync',
  performance: 'Test scores',
  exam_boards: 'Exam boards',
  parent_communication: 'Parent communication',
  referrals: 'Referrals',
  white_label: 'White-label',
  social_login: 'Social login',
  teacher_accounts: 'Teacher accounts',
  email_customisation: 'Custom email',
  reporting: 'Reporting',
  whatsapp_notifications: 'WhatsApp',
}

interface PlanRow {
  id: string
  name: string
  description: string | null
  pricingModel: 'monthly' | 'annual' | 'pay_as_you_go'
  priceInr: number
  studentLimit: number | null
  trialDays: number
  status: 'published' | 'unpublished' | 'deprecated'
  features: Record<string, boolean>
  razorpayPlanId: string | null
}

function SuperPlansPage() {
  const qc = useQueryClient()
  const plans = useQuery({
    queryKey: ['super', 'plans'],
    queryFn: async () => {
      const res = await api<PlanRow[]>('/api/v1/super/plans')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const DEFAULT_DRAFT = {
    name: '',
    description: '',
    pricingModel: 'monthly' as PlanRow['pricingModel'],
    priceInr: 99900,
    studentLimit: 100 as number | null,
    trialDays: 30,
    status: 'unpublished' as PlanRow['status'],
    features: {} as Record<string, boolean>,
  }

  const [draft, setDraft] = useState(DEFAULT_DRAFT)
  // null → create mode; a plan id → editing that plan via PATCH.
  const [editingId, setEditingId] = useState<string | null>(null)

  // Clears the form back to create mode. Used after create/update and on Cancel.
  const resetForm = () => {
    setDraft(DEFAULT_DRAFT)
    setEditingId(null)
  }

  // Load an existing plan into the shared form and switch to edit mode.
  const startEdit = (plan: PlanRow) => {
    setDraft({
      name: plan.name,
      description: plan.description ?? '',
      pricingModel: plan.pricingModel,
      priceInr: plan.priceInr,
      studentLimit: plan.studentLimit,
      trialDays: plan.trialDays,
      status: plan.status,
      features: { ...plan.features },
    })
    setEditingId(plan.id)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const create = useMutation({
    mutationFn: async () => {
      const res = await api<PlanRow>('/api/v1/super/plans', {
        method: 'POST',
        body: JSON.stringify(draft),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (created) => {
      // Append the server-returned plan rather than refetching: an immediate
      // re-read can miss the just-inserted row under read-after-write lag.
      qc.setQueryData<PlanRow[]>(['super', 'plans'], (rows) => [...(rows ?? []), created])
      resetForm()
    },
  })

  const update = useMutation({
    mutationFn: async () => {
      // PATCH /plans/:id takes a partial of every plan field; sending the whole
      // editable draft is fine. NOTE: PATCH does NOT provision a Razorpay plan
      // when status flips to published (only POST does) — publishing stays on
      // the Publish button. Price/model are locked in the UI once a plan is
      // Razorpay-linked, so edit can't break an existing Razorpay link.
      const res = await api<PlanRow>(`/api/v1/super/plans/${editingId}`, {
        method: 'PATCH',
        body: JSON.stringify(draft),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (updated) => {
      qc.setQueryData<PlanRow[]>(['super', 'plans'], (rows) =>
        (rows ?? []).map((p) => (p.id === updated.id ? updated : p)),
      )
      resetForm()
    },
  })

  // The plan being edited (for the Razorpay lock) — price/model are immutable
  // once a Razorpay plan exists for this row.
  const editingPlan = editingId ? plans.data?.find((p) => p.id === editingId) : undefined
  const rzpLocked = !!editingPlan?.razorpayPlanId

  const togglePublish = useMutation({
    mutationFn: async (vars: { id: string; status: PlanRow['status'] }) => {
      const res = await api<PlanRow>(`/api/v1/super/plans/${vars.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: vars.status }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    // Flip the card immediately, then reconcile with the server's returned row.
    // We intentionally do NOT refetch: an eager re-read can hit read-after-write
    // lag (Hyperdrive/Neon) and return the pre-update plan, which would clobber
    // the change and make the status pill snap back to its old value.
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['super', 'plans'] })
      const previous = qc.getQueryData<PlanRow[]>(['super', 'plans'])
      qc.setQueryData<PlanRow[]>(['super', 'plans'], (rows) =>
        (rows ?? []).map((p) => (p.id === vars.id ? { ...p, status: vars.status } : p)),
      )
      return { previous }
    },
    onSuccess: (updated) => {
      qc.setQueryData<PlanRow[]>(['super', 'plans'], (rows) =>
        (rows ?? []).map((p) => (p.id === updated.id ? updated : p)),
      )
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['super', 'plans'], ctx.previous)
    },
  })

  return (
    <div className="space-y-8">
      <PageHeader
        title="Plans"
        description="Define pricing and feature bundles. Publishing a plan creates a matching Razorpay plan."
      />

      <Section title={editingId ? 'Edit plan' : 'Compose a new plan'}>
        <Card>
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" required>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Starter, Growth, Pro …"
                />
              </Field>
              <Field label="Status">
                <Select
                  value={draft.status}
                  onChange={(e) =>
                    setDraft({ ...draft, status: e.target.value as PlanRow['status'] })
                  }
                >
                  <option value="unpublished">Unpublished</option>
                  <option value="published">Published</option>
                  <option value="deprecated">Deprecated</option>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field
                label="Pricing model"
                hint={rzpLocked ? 'Locked: linked to a Razorpay plan.' : undefined}
              >
                <Select
                  value={draft.pricingModel}
                  disabled={rzpLocked}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      pricingModel: e.target.value as PlanRow['pricingModel'],
                    })
                  }
                >
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                  <option value="pay_as_you_go">Pay as you go</option>
                </Select>
              </Field>
              <Field
                label="Price"
                hint={
                  rzpLocked
                    ? 'Locked: a Razorpay plan price cannot change.'
                    : `= ₹${(draft.priceInr / 100).toLocaleString('en-IN')}`
                }
              >
                <Input
                  numeric
                  type="number"
                  value={draft.priceInr}
                  disabled={rzpLocked}
                  onChange={(e) => setDraft({ ...draft, priceInr: Number(e.target.value) })}
                  prefix="₹"
                  suffix="paise"
                />
              </Field>
              <Field label="Trial days">
                <Input
                  numeric
                  type="number"
                  value={draft.trialDays}
                  onChange={(e) => setDraft({ ...draft, trialDays: Number(e.target.value) })}
                  suffix="days"
                />
              </Field>
            </div>
            <Field
              label="Student limit"
              optional
              hint="Leave blank for unlimited."
            >
              <Input
                numeric
                type="number"
                value={draft.studentLimit ?? ''}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    studentLimit: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                placeholder="∞"
              />
            </Field>
            <Field label="Description">
              <Textarea
                rows={2}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="What is this plan for?"
              />
            </Field>

            <div>
              <p className="text-sm font-medium text-fg mb-2">Features included</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4">
                {FEATURE_KEYS.map((key) => (
                  <Checkbox
                    key={key}
                    label={FEATURE_LABELS[key]}
                    checked={!!draft.features[key]}
                    onChange={(next) =>
                      setDraft({
                        ...draft,
                        features: { ...draft.features, [key]: next },
                      })
                    }
                  />
                ))}
              </div>
            </div>
          </CardBody>
          <CardFooter className="flex items-center justify-between">
            <p className="text-xs text-fg-subtle">
              Published plans (not pay-as-you-go) create a Razorpay plan.
            </p>
            <div className="flex items-center gap-2">
              {editingId && (
                <Button variant="ghost" onClick={resetForm} disabled={update.isPending}>
                  Cancel
                </Button>
              )}
              <Button
                onClick={() => (editingId ? update.mutate() : create.mutate())}
                disabled={create.isPending || update.isPending || !draft.name}
                loading={create.isPending || update.isPending}
              >
                {editingId ? 'Save changes' : 'Create plan'}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </Section>

      <Section title="All plans" description={`${plans.data?.length ?? 0} total`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {plans.data?.map((plan) => (
            <Card key={plan.id}>
              <CardHeader
                title={
                  <div className="flex items-center gap-2">
                    <span>{plan.name}</span>
                    <StatusPill
                      tone={
                        plan.status === 'published'
                          ? 'active'
                          : plan.status === 'deprecated'
                            ? 'cancelled'
                            : 'pending'
                      }
                    >
                      {plan.status}
                    </StatusPill>
                  </div>
                }
                description={
                  <span className="capitalize">{plan.pricingModel}</span>
                }
                actions={
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(plan)}>
                      Edit
                    </Button>
                    {plan.status !== 'published' ? (
                      <Button
                        size="sm"
                        onClick={() =>
                          togglePublish.mutate({ id: plan.id, status: 'published' })
                        }
                      >
                        Publish
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          togglePublish.mutate({ id: plan.id, status: 'unpublished' })
                        }
                      >
                        Unpublish
                      </Button>
                    )}
                  </div>
                }
              />
              <CardBody className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <Money paise={plan.priceInr} className="text-2xl font-semibold text-fg" />
                  <p className="text-xs text-fg-subtle">
                    {plan.studentLimit
                      ? `${plan.studentLimit} students`
                      : 'Unlimited'}
                  </p>
                </div>
                <p className="text-xs text-fg-subtle">
                  Razorpay:{' '}
                  {plan.razorpayPlanId ? (
                    <code className="font-mono text-fg-muted">{plan.razorpayPlanId}</code>
                  ) : (
                    <span className="italic">unlinked</span>
                  )}
                </p>
              </CardBody>
            </Card>
          ))}
        </div>
      </Section>
    </div>
  )
}
